import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";
import "./Toast.css";

export type ToastTone = "success" | "warning" | "info" | "danger";

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title?: string;
  message?: ReactNode;
  lines?: string[];
  /** Otomatik kapanma süresi (ms). 0/negatif → otomatik kapanmaz. Verilmezse tona göre varsayılan. */
  duration?: number;
}

const DEFAULT_DURATION: Record<ToastTone, number> = { success: 4500, info: 6000, warning: 9000, danger: 8000 };
const TONE_ICON: Record<ToastTone, "checkCircle" | "alertTriangle" | "bell"> = {
  success: "checkCircle", warning: "alertTriangle", info: "bell", danger: "alertTriangle",
};

function ToastCard({ t, onDismiss }: { t: ToastItem; onDismiss: (id: string) => void }) {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  useEffect(() => {
    const ms = t.duration ?? DEFAULT_DURATION[t.tone];
    if (ms <= 0) return;
    const timer = setTimeout(() => dismissRef.current(t.id), ms);
    return () => clearTimeout(timer);
  }, [t.id, t.tone, t.duration]);

  return (
    <div className={`toast toast--${t.tone}`} role={t.tone === "danger" ? "alert" : "status"}>
      <span className="toast__icon"><Icon name={TONE_ICON[t.tone]} size={16} /></span>
      <div className="toast__body">
        {t.title ? <div className="toast__title">{t.title}</div> : null}
        {t.message ? <div className="toast__msg">{t.message}</div> : null}
        {t.lines && t.lines.length ? (
          <ul className="toast__list">{t.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
        ) : null}
      </div>
      <button type="button" className="toast__close" aria-label="Bildirimi kapat" onClick={() => onDismiss(t.id)}>
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

/** Sağ-üst toast yığını. Üst sınır olmadan büyümesin diye çağıran taraf listeyi (örn. son 5) sınırlamalı. */
export function ToastStack({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (!toasts.length) return null;
  return (
    <div className="toast-stack" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => <ToastCard key={t.id} t={t} onDismiss={onDismiss} />)}
    </div>
  );
}
